import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowRight, FileText, MapPin, Radio, ShieldAlert, ShieldCheck, ShieldQuestion, X } from 'lucide-react';
import { publicCoverageApi } from '../../services/publicApi';
import { flagCode } from '../../utils/flags';
import { countryPath, dialCodeFor } from '../../utils/countryPage';
import { groupColor } from '../../utils/numberGroups';
import { DOCS_BADGE, PUBLIC_GROUPS, docsState, groupDetails, orderUrl, publicGroup } from '../../utils/numberInfo';

const cache = new Map();
const loadCountry = (name) => {
  if (!cache.has(name)) cache.set(name, publicCoverageApi.country(name).catch((e) => { cache.delete(name); throw e; }));
  return cache.get(name);
};

const TONES = {
  green: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-900 ring-amber-200',
  gray: 'bg-gray-100 text-gray-700 ring-gray-200',
};
const TONE_ICON = { green: ShieldCheck, amber: ShieldAlert, gray: ShieldQuestion };

export const DocsBadge = ({ state, size = 'md' }) => {
  const b = DOCS_BADGE[state];
  if (!b) return null;
  const Icon = TONE_ICON[b.tone];
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full ring-1 font-semibold ${TONES[b.tone]} ${size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-xs'}`}>
      <Icon className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />{b.label}
    </span>
  );
};

const SectionHead = ({ icon: Icon, children }) => (
  <h3 className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0a1f2e]/70">
    <Icon className="h-4 w-4 text-[#0a1f2e]" aria-hidden="true" />{children}
  </h3>
);

/**
 * Info dialog for one country's numbers: documents (KYC), address rule, reachability and notes, activation time,
 * outgoing calls, then Order. One tab per number type on offer; the regulations differ by type.
 * Bottom sheet on phones, centred dialog from sm up. Radix handles the focus trap, Esc and aria wiring.
 */
const NumberInfoDialog = ({ country, group, open, onOpenChange, onEnquire }) => {
  const [state, setState] = useState({ name: null, status: 'idle', data: null });
  const [tab, setTab] = useState(group || null);

  useEffect(() => {
    if (!open || !country) return undefined;
    let live = true;
    setState({ name: country, status: 'loading', data: null });
    loadCountry(country)
      .then((data) => live && setState({ name: country, status: 'ok', data }))
      .catch(() => live && setState({ name: country, status: 'error', data: null }));
    return () => { live = false; };
  }, [open, country]);

  const data = state.name === country ? state.data : null;
  const offered = useMemo(() => PUBLIC_GROUPS.filter((g) => groupDetails(data, g.key)?.offered).map((g) => g.key), [data]);
  useEffect(() => {
    if (!offered.length) return;
    setTab((t) => (offered.includes(t) ? t : (offered.includes(group) ? group : offered[0])));
  }, [offered, group]);
  useEffect(() => { if (open) setTab(group || null); }, [open, group, country]);

  const name = data?.country || country || '';
  const dial = dialCodeFor(name);
  const code = flagCode(name);
  const active = offered.includes(tab) ? tab : offered[0];
  const d = groupDetails(data, active);
  const docs = docsState(d);
  const g = publicGroup(active);
  const status = state.name === country ? state.status : 'loading';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[#0a1f2e]/60 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-8 sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[88vh] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:zoom-in-95"
        >
          {/* Header */}
          <div className="relative bg-[#0a1f2e] px-5 pb-4 pt-5 text-white sm:px-6">
            <span className="mx-auto mb-3 block h-1 w-10 rounded-full bg-white/25 sm:hidden" aria-hidden="true" />
            <div className="flex items-start gap-3 pr-10">
              {code && <img src={`https://flagcdn.com/w80/${code}.png`} alt="" width="44" height="32" className="mt-0.5 h-8 w-11 shrink-0 rounded object-cover ring-2 ring-white/20" />}
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#c8ff00]">Number details</p>
                <DialogPrimitive.Title className="font-display text-2xl font-semibold leading-tight">
                  {name}{dial && <span className="ml-2 align-middle font-mono text-sm font-normal text-white/60">{dial}</span>}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="sr-only">
                  Documents, address rule, reachability and ordering for phone numbers in {name}.
                </DialogPrimitive.Description>
              </div>
            </div>
            {docs && <div className="mt-3"><DocsBadge state={docs} /></div>}
            <DialogPrimitive.Close className="absolute right-3 top-3 rounded-full p-2 text-white/70 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff00]" aria-label="Close">
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-y-auto">
            {status === 'loading' && (
              <div className="space-y-3 p-6" aria-live="polite">
                <span className="sr-only">Loading {country} details…</span>
                {[0, 1, 2].map((i) => <div key={i} className="h-5 animate-pulse rounded bg-gray-100" style={{ width: `${80 - i * 15}%` }} />)}
              </div>
            )}
            {status === 'error' && (
              <p className="p-6 text-gray-700">The details for {country} could not be loaded just now. Open the <Link to={countryPath(country)} className="font-semibold underline">{country} page</Link> or try again.</p>
            )}
            {status === 'ok' && d && (
              <>
                {offered.length > 1 && (
                  <div role="tablist" aria-label="Number type" className="flex gap-2 overflow-x-auto border-b border-gray-100 px-5 py-3 sm:px-6">
                    {offered.map((k) => {
                      const sel = k === active;
                      return (
                        <button key={k} type="button" role="tab" aria-selected={sel} aria-controls="number-info-panel" id={`number-info-tab-${k}`}
                          onClick={() => setTab(k)}
                          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff00] ${sel ? 'border-[#0a1f2e] bg-[#0a1f2e] text-white' : 'border-gray-200 text-[#0a1f2e] hover:border-[#0a1f2e]'}`}>
                          <span className={`h-2 w-2 rounded-full ${groupColor(k).dot}`} aria-hidden="true" />{publicGroup(k).label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div id="number-info-panel" role={offered.length > 1 ? 'tabpanel' : undefined} aria-labelledby={offered.length > 1 ? `number-info-tab-${active}` : undefined}
                  className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-x md:divide-y-0">
                  <section className="p-5 sm:p-6">
                    <SectionHead icon={FileText}>Required documents</SectionHead>
                    {d.documents.length ? (
                      <ul className="space-y-2 text-sm text-gray-800">
                        {d.documents.map((doc) => (
                          <li key={doc} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0a1f2e]" aria-hidden="true" />{doc}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-800">
                        {!d.hasRule ? 'We confirm any requirements when you order.' : d.toFollow ? 'The carrier confirms the documents when you order.' : 'No supporting documents required.'}
                      </p>
                    )}
                    {d.address.length > 0 && (
                      <div className="mt-5">
                        <SectionHead icon={MapPin}>Address</SectionHead>
                        {d.address.map((a) => <p key={a} className="text-sm text-gray-800">{a}</p>)}
                      </div>
                    )}
                  </section>
                  <section className="p-5 sm:p-6">
                    <SectionHead icon={Radio}>Reachability &amp; notes</SectionHead>
                    <dl className="space-y-1.5 text-sm">
                      {d.facts.map((f) => (
                        <div key={f.label} className="flex flex-wrap gap-x-1.5"><dt className="text-gray-600">{f.label}:</dt><dd className="font-semibold text-[#0a1f2e]">{f.value}</dd></div>
                      ))}
                      {d.leadTime && <div className="flex flex-wrap gap-x-1.5"><dt className="text-gray-600">Activation:</dt><dd className="font-semibold text-[#0a1f2e]">{d.leadTime}</dd></div>}
                      {d.outbound !== null && (
                        <div className="flex flex-wrap gap-x-1.5"><dt className="text-gray-600">Outgoing calls:</dt><dd className="font-semibold text-[#0a1f2e]">{d.outbound ? 'Available, rates shown when you order' : 'Not available yet'}</dd></div>
                      )}
                    </dl>
                    {d.rules.length > 0 && (
                      <ul className="mt-4 space-y-1.5 text-sm text-gray-700">
                        {d.rules.map((r) => <li key={r} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" aria-hidden="true" />{r}</li>)}
                      </ul>
                    )}
                    {!d.facts.length && !d.leadTime && d.outbound === null && !d.rules.length && (
                      <p className="text-sm text-gray-600">No further notes for these numbers.</p>
                    )}
                  </section>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-gray-100 bg-[#f5f7f9] px-5 py-4 sm:px-6">
            <a href={orderUrl(name, active)}
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#0a1f2e] px-5 py-3 text-center font-semibold text-white transition hover:bg-[#12324a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff00] focus-visible:ring-offset-2">
              Order {g ? `a ${g.label} number in ${name}` : `a ${name} number`}
              <ArrowRight className="h-4 w-4 text-[#c8ff00] transition group-hover:translate-x-0.5" aria-hidden="true" />
            </a>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm">
              <Link to={countryPath(name)} onClick={() => onOpenChange(false)} className="font-semibold text-[#0a1f2e] underline decoration-[#c8ff00] decoration-2 underline-offset-4">{name} numbers page</Link>
              <Link to={countryPath(name, '/regulatory-requirements')} onClick={() => onOpenChange(false)} className="font-semibold text-[#0a1f2e] underline decoration-[#c8ff00] decoration-2 underline-offset-4">Regulatory requirements</Link>
            </div>
            <p className="mt-2 text-center text-xs text-gray-600">
              You order in the VOIP2 customer portal.{' '}
              {onEnquire
                ? <>New to VOIP2? <button type="button" className="font-semibold underline" onClick={() => { onOpenChange(false); onEnquire(name, g?.label); }}>Ask us about {name}</button></>
                : <>New to VOIP2? <Link to="/contact" className="font-semibold underline">Talk to us</Link></>}
            </p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default NumberInfoDialog;
